/*
  STR8 CARD TILT v1.0 — pointer-driven 3D tilt for a held object

  Built for the invitation card on the invite-only page. That card is meant to
  read as a physical thing someone handed you, and a static render never does.
  It tilts toward the cursor on a desktop, and it can be picked up and turned
  with a finger on a phone.

  WHY THIS IS NOT PART OF str8-parallax.js
  The parallax engine owns the transform property of every element carrying
  data-str8-pin-layer and rewrites it on every scroll frame. Two owners of one
  transform means the last writer wins and the other silently disappears. So
  the two live on two separate elements: the pin wrapper takes the scroll-driven
  exit, this takes the pointer-driven tilt, and the browser composes them. That
  is the same rule the house already applies to a revealed element that also
  carries a hover transform.

  PERSPECTIVE
  rotateX and rotateY do nothing without a perspective on an ancestor. It lives
  on .str8-inv__card-stage in CSS and must never be written into this element's
  own transform, because this script rewrites that property every frame.

  TOUCH, AND WHY IT IS pan-y
  The card sits dead centre of a pinned full-height hero. An element in that
  position that swallows vertical drags traps the reader on a phone, with no
  way to scroll past. So the CSS sets touch-action: pan-y: the browser keeps
  vertical panning and hands us the gesture only once it is clearly horizontal.
  A sideways drag turns the card, an up or down drag scrolls the page, and a
  gesture the browser reclaims arrives here as pointercancel and springs back.

  NOT A PERMANENT rAF LOOP
  Frames are scheduled only while the card is being pointed at, dragged, or
  settling back to rest. Once it is level and untouched, nothing is running.
*/
(function () {
  'use strict';

  if (window.__str8CardTiltLoaded) return;
  window.__str8CardTiltLoaded = true;

  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }

  function Tilt(el) {
    this.el = el;
    this.maxY = parseFloat(el.getAttribute('data-str8-tilt-max-y')) || 16;
    this.maxX = parseFloat(el.getAttribute('data-str8-tilt-max-x')) || 12;

    /* Current rendered angles, and where they are heading. Kept apart so the
       card eases toward a target rather than snapping to the pointer, which is
       what makes it feel like it has mass. */
    this.rx = 0; this.ry = 0;
    this.tx = 0; this.ty = 0;

    this.dragging = false;
    this.pointerId = null;
    this.startX = 0; this.startY = 0;
    this.baseX = 0; this.baseY = 0;
    this.pending = false;

    this.frame = this.draw.bind(this);
    this.bind();
  }

  Tilt.prototype.bind = function () {
    var self = this;
    var el = this.el;

    el.addEventListener('pointerdown', function (e) {
      if (self.dragging) return;
      self.dragging = true;
      self.pointerId = e.pointerId;
      self.startX = e.clientX;
      self.startY = e.clientY;
      /* Continue from wherever the card already is, so picking it up a second
         time does not jump it back to level first. */
      self.baseX = self.tx;
      self.baseY = self.ty;
      el.classList.add('is-held');
      /* Keeps the gesture attached to this element even if the finger leaves
         it, so the card does not stick mid-turn when the pointer slides off. */
      if (el.setPointerCapture) { try { el.setPointerCapture(e.pointerId); } catch (err) {} }
      self.request();
    });

    el.addEventListener('pointermove', function (e) {
      if (self.dragging) {
        if (e.pointerId !== self.pointerId) return;
        /* Roughly a quarter of a turn across the width of the card. Dividing
           by the card's own size keeps the feel identical at 375px and on a
           desktop where the card is far wider. */
        var r = el.getBoundingClientRect();
        var dx = (e.clientX - self.startX) / (r.width || 1);
        var dy = (e.clientY - self.startY) / (r.height || 1);
        self.ty = clamp(self.baseY + dx * self.maxY * 2.4, -self.maxY, self.maxY);
        self.tx = clamp(self.baseX - dy * self.maxX * 2.4, -self.maxX, self.maxX);
        self.request();
        return;
      }
      /* Hover follow, mouse and stylus only. A touch pointer generates a move
         before its tap and would flick the card on a plain scroll past. */
      if (e.pointerType === 'touch') return;
      var b = el.getBoundingClientRect();
      if (!b.width || !b.height) return;
      self.ty = clamp(((e.clientX - b.left) / b.width - 0.5) * 2 * self.maxY, -self.maxY, self.maxY);
      self.tx = clamp(-((e.clientY - b.top) / b.height - 0.5) * 2 * self.maxX, -self.maxX, self.maxX);
      self.request();
    });

    function release(e) {
      if (self.dragging && e && e.pointerId !== self.pointerId) return;
      self.dragging = false;
      self.pointerId = null;
      el.classList.remove('is-held');
      /* Back to level. The easing in draw() carries it, so the return is the
         same spring the tilt itself uses rather than a separate animation. */
      self.tx = 0; self.ty = 0;
      self.request();
    }

    el.addEventListener('pointerup', release);
    el.addEventListener('pointercancel', release);
    el.addEventListener('pointerleave', function (e) {
      if (self.dragging) return;
      release(e);
    });
  };

  Tilt.prototype.request = function () {
    if (this.pending) return;
    this.pending = true;
    requestAnimationFrame(this.frame);
  };

  Tilt.prototype.draw = function () {
    this.pending = false;

    /* Ease toward the target. Faster while held so the card tracks the finger,
       slower on release so it settles rather than snapping level. */
    var k = this.dragging ? 0.34 : 0.12;
    this.rx += (this.tx - this.rx) * k;
    this.ry += (this.ty - this.ry) * k;

    var restX = Math.abs(this.tx - this.rx);
    var restY = Math.abs(this.ty - this.ry);

    /* Below a twentieth of a degree nothing is visible. Land exactly on the
       target and stop scheduling frames rather than easing toward it forever. */
    if (restX < 0.05 && restY < 0.05) {
      this.rx = this.tx;
      this.ry = this.ty;
    }

    this.el.style.transform =
      'rotateX(' + this.rx.toFixed(3) + 'deg) rotateY(' + this.ry.toFixed(3) + 'deg)';

    /* Drives the specular gleam in CSS. The highlight slides opposite the turn,
       which is what a flat glossy surface actually does under a fixed light. */
    this.el.style.setProperty('--str8-gleam-x', (50 - (this.ry / this.maxY) * 42).toFixed(2) + '%');
    this.el.style.setProperty('--str8-gleam-y', (50 + (this.rx / this.maxX) * 42).toFixed(2) + '%');

    if (this.rx !== this.tx || this.ry !== this.ty) this.request();
  };

  function init() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    var els = document.querySelectorAll('[data-str8-tilt]');
    for (var i = 0; i < els.length; i++) {
      if (els[i].__str8Tilt) continue;
      els[i].__str8Tilt = new Tilt(els[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
